/**
 * Foundation/Core — NotificationDetailDialog
 *
 * Dialog di dettaglio generico per una singola notifica. Non conosce il dominio:
 * riceve un oggetto `meta` gia' risolto dal chiamante (Product Pack) con la
 * struttura standard prodotta da `getNotificationMeta` — vedi contratto sotto.
 *
 * CONTRATTO `meta`:
 * {
 *   id, kind, isRead, createdAt,
 *   severity: { key, label, tone, hex, dot },
 *   module,
 *   title, body, subject,
 *   detailFields: [{ label, value }],
 *   cta: { label, to, testid } | null,
 * }
 *
 * Il componente rende:
 * - header colorato per severita' (icona + badge + titolo + sottotitolo module)
 * - lista `detailFields` (label/valore)
 * - riga "Ricevuta" con `createdAt` formattato via Foundation
 * - footer con azioni: Elimina, Chiudi, CTA (se presente)
 *
 * Utilizzabile da qualsiasi Product Pack: se il pack passa `meta` conforme al
 * contratto, il rendering e' identico senza modifiche al CORE.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Trash2, X, AlertOctagon, AlertTriangle, Info } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtDateTimeFull } from "./formatters";

function SeverityIcon({ tone, className }) {
  if (tone === "critical") return <AlertOctagon className={className} />;
  if (tone === "warning") return <AlertTriangle className={className} />;
  return <Info className={className} />;
}

export function NotificationDetailDialog({ meta, open, onClose, onDelete }) {
  const navigate = useNavigate();
  if (!meta) return null;

  const sev = meta.severity;
  const severityBg =
    sev.key === "critical" ? "bg-red-50 border-red-200 text-red-800"
    : sev.key === "warning" ? "bg-amber-50 border-amber-200 text-amber-800"
    : "bg-emerald-50 border-emerald-200 text-emerald-800";

  const handleCta = () => {
    if (meta.cta?.to) {
      navigate(meta.cta.to);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden" data-testid="notification-detail-dialog">
        <div className={`px-5 py-4 border-b flex items-start gap-3 ${severityBg}`}>
          <SeverityIcon tone={sev.key} className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="text-[10px] uppercase tracking-[0.14em] font-bold"
                data-testid="notification-severity-badge"
              >
                {sev.label}
              </span>
              <span className="text-[10px] uppercase tracking-[0.14em] font-semibold opacity-70">
                · {meta.module}
              </span>
            </div>
            <DialogHeader className="p-0">
              <DialogTitle className="text-base font-semibold leading-tight text-slate-900"
                           data-testid="notification-detail-title">
                {meta.title}
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-slate-700 mt-1" data-testid="notification-detail-body">
              {meta.body}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-2.5" data-testid="notification-detail-fields">
          {meta.detailFields.map((f) => (
            <div key={f.label} className="flex items-start gap-3 text-sm">
              <div className="w-32 flex-shrink-0 text-[11px] uppercase tracking-wider text-slate-500 font-semibold pt-0.5">
                {f.label}
              </div>
              <div className="flex-1 text-slate-800 font-medium">{f.value}</div>
            </div>
          ))}
          <div className="flex items-start gap-3 text-sm pt-2 border-t border-slate-100">
            <div className="w-32 flex-shrink-0 text-[11px] uppercase tracking-wider text-slate-500 font-semibold pt-0.5">
              Ricevuta
            </div>
            <div className="flex-1 text-slate-800 font-medium" data-testid="notification-detail-datetime">
              {fmtDateTimeFull(meta.createdAt)}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { onDelete(meta.id); onClose(); }}
            className="text-slate-500 hover:text-red-700 hover:bg-red-50"
            data-testid="notification-detail-delete"
          >
            <Trash2 size={14} className="mr-1.5" /> Elimina
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              data-testid="notification-detail-close"
            >
              <X size={14} className="mr-1.5" /> Chiudi
            </Button>
            {meta.cta && (
              <Button
                type="button"
                size="sm"
                onClick={handleCta}
                className="bg-suite-primary hover:bg-suite-primary-hover text-white"
                data-testid={meta.cta.testid || "notification-detail-cta"}
              >
                <ExternalLink size={14} className="mr-1.5" /> {meta.cta.label}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NotificationDetailDialog;
